import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { CatalogTable } from '@/features/catalog/Catalog';
import type { GiftCardBody, ShopSettingsBody } from '@/shared/api/generated/model';
import {
  shopGiftCardIssue,
  shopGiftCards,
  shopGiftCardToggle,
} from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { CopyButton } from '@/shared/ui/CopyButton';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';
import { giftState, money } from './shop';

/**
 * Подарочные сертификаты (шаг 9.10): купленные на сайте и выпущенные вручную —
 * например, бумажные из розницы. Код виден целиком: его диктуют покупателю.
 */
export function GiftCardsTab({
  siteId,
  env,
  shop,
}: {
  siteId: string;
  env: EnvKey;
  shop: ShopSettingsBody;
}) {
  const queryClient = useQueryClient();
  const [issuing, setIssuing] = useState(false);
  const key = ['shop-gift-cards', siteId, env];

  const cards = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await shopGiftCards(siteId, kinds[env]);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список сертификатов');
      }

      return response.data.items ?? [];
    },
  });

  const items = cards.data ?? [];
  const selling = shop.gift_cards?.enabled === true;

  const toggle = (card: GiftCardBody) => {
    shopGiftCardToggle(card.id, { disabled: !card.disabled })
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: key });
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось изменить сертификат');
      });
  };

  return (
    <div className={styles['list']}>
      <p className={styles['muted']}>
        {selling
          ? `На сайте продаются: от ${money(shop.gift_cards?.min ?? 0)} до ${money(shop.gift_cards?.max ?? 0)}${
              (shop.gift_cards?.months ?? 0) > 0
                ? `, действуют ${String(shop.gift_cards?.months)} мес.`
                : ', бессрочно'
            }. Код уходит получателю письмом после оплаты.`
          : 'На сайте сертификаты не продаются — включите продажу в «Настройках». Выпустить вручную можно и без неё.'}
      </p>

      <div>
        <Button
          iconLeft={<Plus size={15} />}
          onClick={() => {
            setIssuing(true);
          }}
        >
          Выпустить сертификат
        </Button>
      </div>

      {cards.isPending ? null : items.length === 0 ? (
        <EmptyState
          icon={<Gift />}
          title="Сертификатов пока нет"
          description="Купленные на сайте появятся здесь после оплаты. Бумажный из розницы — выпустите вручную."
        />
      ) : (
        <CatalogTable head={['Код', 'Остаток', 'Получатель', 'Действует до', '']}>
          {items.map((card) => {
            const state = giftState(card);

            return (
              <tr key={card.id}>
                <td>
                  <div className={styles['panel__actions']}>
                    <span className={styles['mono']}>{card.code}</span>
                    <CopyButton value={card.code} label="Скопировать код" />
                  </div>
                </td>
                <td>
                  {money(card.balance, card.currency)}
                  {card.balance !== card.initial && (
                    <div className={styles['muted']}>из {money(card.initial, card.currency)}</div>
                  )}
                </td>
                <td>
                  {card.recipient_name || card.recipient_email || '—'}
                  {card.recipient_name !== '' && card.recipient_email !== '' && (
                    <div className={styles['muted']}>{card.recipient_email}</div>
                  )}
                </td>
                <td>
                  {card.expires_at === undefined
                    ? 'бессрочно'
                    : new Date(card.expires_at).toLocaleDateString('ru-RU')}
                </td>
                <td>
                  <div className={styles['panel__actions']}>
                    {state !== undefined && (
                      <Badge size="sm" tone={state.tone}>
                        {state.label}
                      </Badge>
                    )}
                    {card.order_id === undefined && (
                      <Badge size="sm" tone="neutral">
                        вручную
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        toggle(card);
                      }}
                    >
                      {card.disabled ? 'Включить' : 'Отключить'}
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </CatalogTable>
      )}

      {issuing && (
        <IssueDialog
          siteId={siteId}
          env={env}
          onClose={() => {
            setIssuing(false);
          }}
          onIssued={() => {
            setIssuing(false);
            void queryClient.invalidateQueries({ queryKey: key });
          }}
        />
      )}
    </div>
  );
}

function IssueDialog({
  siteId,
  env,
  onClose,
  onIssued,
}: {
  siteId: string;
  env: EnvKey;
  onClose: () => void;
  onIssued: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [notify, setNotify] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const value = Number(amount.replace(',', '.'));

    if (!(value > 0)) {
      setError('Номинал — больше нуля');

      return;
    }

    setBusy(true);
    setError(undefined);

    shopGiftCardIssue(siteId, kinds[env], {
      amount: value,
      recipient_name: name.trim(),
      recipient_email: email.trim(),
      message: message.trim(),
      notify,
    })
      .then((response) => {
        if (response.status === 201) {
          toast.success(`Сертификат ${response.data.code} выпущен`);
        }

        onIssued();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось выпустить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        title="Выпустить сертификат"
        size="sm"
        dismissible={false}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" form="gift-form" loading={busy}>
              Выпустить
            </Button>
          </>
        }
      >
        <form id="gift-form" className={styles['form']} onSubmit={submit} noValidate>
          <Field label="Номинал, ₽" hint="Срок действия — из настроек магазина">
            <Input
              inputMode="decimal"
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
              }}
            />
          </Field>
          <Field label="Получатель">
            <Input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </Field>
          <Field label="Почта получателя">
            <Input
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Field>
          <Field label="Пожелание">
            <Input
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
              }}
            />
          </Field>
          <Switch checked={notify} onCheckedChange={setNotify}>
            Отправить код письмом — иначе он только здесь (бумажный сертификат)
          </Switch>
          {error !== undefined && <p className={styles['error']}>{error}</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
}
